import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { JsonAdvancedSearchResult, NodeServiceResourceService } from '@psnb/nwt-api';
import { GeneratorMobileSearch } from '@psnb/psnb-api';
import { GeneratorService, isDefinedNotNull, isEmpty, isUndefinedOrNull } from '@psnb/services';
import { PowerSupplyAssetType } from '../power-supply-generator-asset-details/power-supply-asset-type.enum';
import { RefuelRecoveryGeneratorDialogComponent } from '../power-supply-telemetry/refuel-recovery-generator-dialog/refuel-recovery-generator-dialog.component';
import { RefuelRecoverAction } from '../power-supply-telemetry/telemetry-generator.model';
import { SearchDataDeployGeneratorDialogComponent } from './deploy-generator-dialog/deploy-generator-dialog.component';
import { EnhancedGeneratorMobileSearch, GeneratorSearchDataViewModel } from './generator-searchdata.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'psnb-power-supply-generator-searchdata',
  styleUrls: ['./power-supply-generator-searchdata.component.scss'],
  templateUrl: './power-supply-generator-searchdata.component.html'
})
export class PowerSupplyGeneratorSearchDataComponent implements OnInit, OnDestroy {
  public vm: GeneratorSearchDataViewModel;

  private readonly FUEL_TANK_DISPLAY_TEXT: string = 'Fuel Tank';
  private readonly userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  private deployGeneratorDialogRef: MatDialogRef<SearchDataDeployGeneratorDialogComponent> | undefined;
  private destroyed$: Subject<void>;
  private nodeText: string | undefined;
  private params: {
    assetId: string | undefined;
    make: string | undefined;
    model: string | undefined;
    node: string | undefined;
    nodeCommunintyServed: string | undefined;
    nodeId: number | undefined;
    nwtTeam: string | undefined;
  };
  private refuelRecoveryDialogRef: MatDialogRef<RefuelRecoveryGeneratorDialogComponent> | undefined;
  private searchResult: JsonAdvancedSearchResult[];

  public constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private generatorService: GeneratorService,
    private nodeService: NodeServiceResourceService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.destroyed$ = new Subject<void>();
  }

  public ngOnInit(): void {
    this.vm = {
      rowData: [],
      showLoading: false
    };

    this.params = {
      assetId: undefined,
      make: undefined,
      model: undefined,
      node: undefined,
      nodeCommunintyServed: undefined,
      nodeId: undefined,
      nwtTeam: undefined
    };

    this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params: { [key: string]: string }): void => {
        this.params.assetId = params.assetId;
        this.params.make = params.make;
        this.params.model = params.model;
        this.params.node = params.node;
        this.params.nodeCommunintyServed = params.nodeCommunintyServed;
        this.params.nodeId = params.nodeId ? Number(params.nodeId) : undefined;
        this.params.nwtTeam = params.nwtTeam;
      });

    // Load initial data based on query parameters
    this.loadGeneratorData();
  }

  public ngOnDestroy(): void {
    this.deployGeneratorDialogRef?.close();
    this.refuelRecoveryDialogRef?.close();
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  /**
   * Maps GeneratorMobileSearch array to EnhancedGeneratorMobileSearch array.
   * Normalizes powerSupplyId to string | undefined for consistent template binding.
   *
   * @param {GeneratorMobileSearch[]} items - The generator mobile search results from the API
   * @returns {EnhancedGeneratorMobileSearch[]} Enhanced generator mobile search results with normalized powerSupplyId
   */
  private mapToEnhancedRows(items: GeneratorMobileSearch[]): EnhancedGeneratorMobileSearch[] {
    return items.map((item: GeneratorMobileSearch): EnhancedGeneratorMobileSearch => ({
      ...item,
      powerSupplyId: isDefinedNotNull(item.powerSupplyId) ? item.powerSupplyId.toString() : undefined
    }));
  }

  private loadGeneratorData(): void {
    this.nodeText = this.route.snapshot.queryParams.nodeText;
    if (isDefinedNotNull(this.params.assetId)) {
      this.loadByAssetId();
    } else if (isDefinedNotNull(this.params.nwtTeam)) {
      this.loadByNwtTeam();
    } else if (isDefinedNotNull(this.params.node)) {
      this.loadByNode();
    } else if (isDefinedNotNull(this.params.make)) {
      this.loadByMake();
    } else if (isDefinedNotNull(this.params.model)) {
      this.loadByModel();
    } else if (isDefinedNotNull(this.nodeText)) {
      this.loadByNodeText();
    }
  }

  private loadByAssetId(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.generatorService.advanceSearchByAssetIDMobile(this.params.assetId, this.userTimeZone)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorMobileSearch[]): void => {
          const assetIdToMatch: string = String(this.params.assetId);
          const generatorList: GeneratorMobileSearch[] = Array.isArray(response) ? response : [];
          const searchValues: Array<keyof GeneratorMobileSearch> =
            ['assetId', 'dbId', 'fuelTankId', 'generatorId', 'localId', 'qrCodeAssetId', 'trailerId'];
          const filteredList: GeneratorMobileSearch[] = generatorList.filter((item: GeneratorMobileSearch): boolean =>
            searchValues.some((searchItem: keyof GeneratorMobileSearch): boolean =>
              item[searchItem]?.toString() === assetIdToMatch
            )
          );
          this.vm.rowData = this.mapToEnhancedRows(filteredList);
          this.cdr.detectChanges();
        }
      );
  }

  private loadByNwtTeam(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.generatorService.advanceSearchByNwtTeamMobile(this.params.nwtTeam, this.userTimeZone)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorMobileSearch[]): void => {
          const generatorList: GeneratorMobileSearch[] = Array.isArray(response) ? response : [];
          this.vm.rowData = this.mapToEnhancedRows(generatorList);
          this.cdr.detectChanges();
        }
      );
  }

  private loadByNode(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.generatorService.advanceSearchByNodeMobile(
      this.params.nodeId,
      this.params.nodeCommunintyServed,
      this.params.node,
      this.userTimeZone
    )
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorMobileSearch[]): void => {
          const generatorList: GeneratorMobileSearch[] = Array.isArray(response) ? response : [];
          this.vm.rowData = this.mapToEnhancedRows(generatorList);
          this.cdr.detectChanges();
        }
      );
  }

  private loadByMake(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.generatorService.advanceSearchByMakeMobile(this.params.make, this.userTimeZone)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorMobileSearch[]): void => {
          const generatorList: GeneratorMobileSearch[] = Array.isArray(response) ? response : [];
          this.vm.rowData = this.mapToEnhancedRows(generatorList);
          this.cdr.detectChanges();
        }
      );
  }

  private loadByModel(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.generatorService.advanceSearchByModelMobile(this.params.model, this.userTimeZone)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorMobileSearch[]): void => {
          const generatorList: GeneratorMobileSearch[] = Array.isArray(response) ? response : [];
          this.vm.rowData = this.mapToEnhancedRows(generatorList);
          this.cdr.detectChanges();
        }
      );
  }

  private loadByNodeText(): void {
    this.vm.showLoading = true;
    this.cdr.detectChanges();
    this.nodeService.resourceNodeServiceResourceAdvancedSearchGET(this.nodeText)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: JsonAdvancedSearchResult[]): void => {
          this.searchResult = response;
          if (isEmpty(this.searchResult)) {
            this.vm.rowData = [];
            this.cdr.detectChanges();
          } else {
            this.generatorService.advanceSearchByNodeMobile(
              this.searchResult[0].nodeId,
              this.searchResult[0].nodeCommunintyServed,
              this.searchResult[0].node,
              this.userTimeZone
            )
              .pipe(takeUntil(this.destroyed$))
              .subscribe(
                (generatorResponse: GeneratorMobileSearch[]): void => {
                  const generatorList: GeneratorMobileSearch[] = Array.isArray(generatorResponse)
                    ? generatorResponse
                    : [];
                  this.vm.rowData = this.mapToEnhancedRows(generatorList);
                  this.cdr.detectChanges();
                }
              );
          }
        }
      );
  }

  /**
   * Navigates to the asset details page for the selected asset.
   *
   * @param {EnhancedGeneratorMobileSearch} searchData - The asset data containing navigation parameters
   * @returns {void}
   */
  public assetDetails(searchData: EnhancedGeneratorMobileSearch): void {
    const {qrCodeAssetId, dbId, assetType } = searchData;
    if (isDefinedNotNull(assetType)) {
      switch (assetType) {
        case PowerSupplyAssetType.Generator:
        case PowerSupplyAssetType.Trailer:
          this.router.navigate(['/assetdetails'],
            { queryParams: { assetId: qrCodeAssetId, assetType, dbId } });
          break;
        case this.FUEL_TANK_DISPLAY_TEXT:
          this.router.navigate(['/assetdetails'],
            { queryParams: { assetId: qrCodeAssetId, assetType: PowerSupplyAssetType.FuelTank, dbId } });
          break;
        default:
          break;
      }
    }
  }

  /**
   * Navigates to the asset edit page for the selected asset.
   *
   * @param {EnhancedGeneratorMobileSearch} searchData - The asset data containing navigation parameters
   * @returns {void}
   */
  public assetEdit(searchData: EnhancedGeneratorMobileSearch): void {
    const {qrCodeAssetId, dbId, assetType } = searchData;

    if (isDefinedNotNull(assetType)) {
      switch (assetType) {
        case PowerSupplyAssetType.Generator:
        case PowerSupplyAssetType.Trailer:
          this.router.navigate(['/assetedit'], { queryParams: { assetType, dbId, qrCodeAssetId } });
          break;
        case this.FUEL_TANK_DISPLAY_TEXT:
          this.router.navigate(['/assetedit'],
            { queryParams: { assetType: PowerSupplyAssetType.FuelTank, dbId, qrCodeAssetId } });
          break;
        default:
          break;
      }
    }
  }

  /**
   * Navigates to the power supply details (telemetry) page.
   *
   * @param {string | undefined} powerSupplyId - The power supply ID to view details for
   * @returns {void}
   */
  public powerSupplyDetails(powerSupplyId: string | undefined): void {
    if (isDefinedNotNull(powerSupplyId)) {
      this.router.navigate(['/telemetry'], { queryParams: { powerSupplyId } });
    }
  }

  /**
   * Opens the Deploy Generator dialog for the selected generator.
   * Passes generator identifiers (dbId, localId, qrCodeAssetId, serialNumber) to the dialog.
   * Reloads generator data after successful deployment.
   *
   * @param {EnhancedGeneratorMobileSearch} searchData - The generator data to deploy
   * @returns {void}
   */
  public openDeployGeneratorDialog(searchData: EnhancedGeneratorMobileSearch): void {
    if (isDefinedNotNull(this.deployGeneratorDialogRef?.getState) &&
        this.deployGeneratorDialogRef.getState() !== 0) {
      return;
    }

    this.deployGeneratorDialogRef = this.dialog.open(SearchDataDeployGeneratorDialogComponent, {
      data: {
        dbId: searchData.dbId,
        localId: searchData.localId,
        qrCodeAssetId: searchData.qrCodeAssetId,
        serialNumber: searchData.serialNumber
      },
      disableClose: true,
      panelClass: 'my-centered-dialog'
    });

    this.deployGeneratorDialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((deploymentResult: boolean): void => {
        if (deploymentResult) {
          // Reload the generator data to reflect the updated deployment status
          this.loadGeneratorData();
        }
        this.deployGeneratorDialogRef = undefined;
      });
  }

  /**
   * Opens the Refuel or Recover Generator dialog for the selected generator.
   * Requires a valid generator ID from the search data.
   * Reloads generator data after successful refuel/recovery.
   *
   * @param {EnhancedGeneratorMobileSearch} searchData - The generator data
   * @param {RefuelRecoverAction} action - The action to perform (Refuel or Recover)
   * @returns {void}
   */
  public openRefuelRecoverDialog(
    searchData: EnhancedGeneratorMobileSearch,
    action: RefuelRecoverAction
  ): void {
    if (isDefinedNotNull(this.refuelRecoveryDialogRef?.getState) &&
        this.refuelRecoveryDialogRef.getState() !== 0) {
      return;
    }

    const generatorId: string | undefined = searchData.dbId?.toString();

    if (!isDefinedNotNull(generatorId)) {
      return;
    }

    this.refuelRecoveryDialogRef = this.dialog.open(RefuelRecoveryGeneratorDialogComponent, {
      data: { action, generatorId },
      disableClose: true,
      panelClass: 'my-centered-dialog'
    });

    this.refuelRecoveryDialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((refuelRecoveryResult: boolean | undefined): void => {
        if (refuelRecoveryResult) {
          // Reload the generator data to reflect the updated status
          this.loadGeneratorData();
        }
        this.refuelRecoveryDialogRef = undefined;
      });
  }

  /**
   * TrackBy function for ngFor to improve performance by tracking items by assetId.
   *
   * @param {number} index - The index of the item in the array
   * @param {EnhancedGeneratorMobileSearch} item - The current item being tracked
   * @returns {string | number} The unique identifier for the item
   */
  public trackByAssetId(index: number, item: EnhancedGeneratorMobileSearch): string | number {
    return item.assetId ?? index;
  }

  /**
   * Converts a refuel due date to a Date object for template formatting.
   * Handles both array format [year, month, day, hour, minute, second, nanoseconds] and string format.
   * Array format month is 1-indexed (1 = January), converted to JavaScript's 0-indexed format.
   * Returns undefined if the input is undefined or cannot be parsed into a valid date.
   *
   * @param {string | number[] | undefined} dateValue - The date value to convert. Can be:
   *   - Array: [year, month (1-indexed), day, hour?, minute?, second?, nanoseconds?]
   *   - String: Any valid date string format (e.g., ISO 8601)
   *   - undefined: Returns undefined
   * @returns {Date | undefined} The Date object if valid, or undefined if the input is invalid or cannot be parsed
   */
  public parseRefuelDueDate(dateValue: string | number[] | undefined): Date | undefined {
    if (isUndefinedOrNull(dateValue)) {
      return undefined;
    }

    // Handle array format: [year, month, day, hour, minute, second, nanoseconds]
    if (Array.isArray(dateValue) && dateValue.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nanoseconds = 0] = dateValue;
      // Note: JavaScript Date month is 0-indexed (0 = January), so subtract 1 from the month value
      const date: Date = new Date(year, month - 1, day, hour, minute, second, Math.floor(nanoseconds / 1000000));
      return Number.isNaN(date.getTime()) ? undefined : date;
    }

    // Handle string format
    if (typeof dateValue === 'string') {
      const date: Date = new Date(dateValue);
      return Number.isNaN(date.getTime()) ? undefined : date;
    }

    return undefined;
  }
}
